import { Link } from '@tanstack/react-router';
import { AlertTriangle, Clock, DollarSign, type LucideIcon } from 'lucide-react';

import { AdminLayout } from '@/app/shell/admin-layout';
import {
  type AiUsageSummary,
  type SubscriptionBreakdown,
  type SubscriptionNeedingAttention,
  type SubscriptionStatus,
  useMetricsAiUsage,
  useMetricsPlanDistribution,
  useMetricsSubscriptionBreakdown,
  useMetricsSubscriptionsNeedingAttention,
  useMetricsSummary,
  useMetricsTimeseries,
  useMetricsTopStorage,
} from '@/shared/api/admin-metrics';

export function MetricsPage() {
  const { data: summary } = useMetricsSummary();
  const { data: timeseries } = useMetricsTimeseries(30);
  const { data: distribution } = useMetricsPlanDistribution();
  const { data: topStorage } = useMetricsTopStorage(10);
  const { data: subBreakdown } = useMetricsSubscriptionBreakdown();
  const { data: needsAttention } = useMetricsSubscriptionsNeedingAttention();
  const { data: aiUsage } = useMetricsAiUsage();

  return (
    <AdminLayout>
      <header className="mb-6">
        <h1 className="text-xl font-semibold">Metrics</h1>
        <p className="text-sm text-neutral-400">
          Live snapshots.  Cached 5 minutes — admin dashboard tolerates lag.
        </p>
      </header>

      {/* Summary tiles */}
      <section className="mb-6 grid gap-3 md:grid-cols-3">
        <Tile label="Total users" value={summary?.totalUsers ?? '…'} />
        <Tile label="Total teams" value={summary?.totalTeams ?? '…'} />
        <Tile label="Active subscriptions" value={summary?.activeSubscriptions ?? '…'} />
        <Tile label="Manual grants" value={summary?.manualGrants ?? '…'} />
        <Tile
          label="Total storage used"
          value={summary ? formatBytes(summary.totalStorageBytes) : '…'}
        />
        <Tile
          label="Pseudo-MRR (potential)"
          value={summary ? `$${(summary.pseudoMrrCents / 100).toFixed(2)}` : '…'}
          hint="Sum of unit_amount × seat_count for active monthly subscriptions"
        />
      </section>

      {/* Timeseries chart (SVG sparkline) */}
      <section className="mb-6 rounded-lg border border-neutral-800 bg-neutral-900 p-5">
        <h2 className="mb-3 text-sm font-medium text-neutral-300">Last 30 days</h2>
        {timeseries && timeseries.length > 0 ? (
          <TimeseriesChart data={timeseries} />
        ) : (
          <p className="text-sm text-neutral-500">No activity yet.</p>
        )}
      </section>

      {/* AI usage + cost this month (both ledgers — user-facing + agents) */}
      <AiUsageSection usage={aiUsage} />

      {/* Subscription health: status breakdown + MRR per plan + actionable counters */}
      <SubscriptionHealthSection
        breakdown={subBreakdown}
        needsAttention={needsAttention}
      />

      <section className="grid gap-4 md:grid-cols-2">
        {/* Plan distribution */}
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
          <h2 className="mb-3 text-sm font-medium text-neutral-300">Plan distribution</h2>
          {distribution && distribution.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="py-1 font-medium">Plan</th>
                  <th className="py-1 text-right font-medium">Teams</th>
                </tr>
              </thead>
              <tbody>
                {distribution.map((d) => (
                  <tr key={d.planCode}>
                    <td className="py-1">
                      <code className="text-orange-400">{d.planCode}</code>
                    </td>
                    <td className="py-1 text-right text-neutral-200">{d.teamCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-neutral-500">Loading…</p>
          )}
        </div>

        {/* Top storage teams */}
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
          <h2 className="mb-3 text-sm font-medium text-neutral-300">Top 10 by storage</h2>
          {topStorage && topStorage.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="py-1 font-medium">Team</th>
                  <th className="py-1 font-medium">Plan</th>
                  <th className="py-1 text-right font-medium">Used</th>
                </tr>
              </thead>
              <tbody>
                {topStorage.map((t) => (
                  <tr key={t.teamId}>
                    <td className="py-1">
                      <Link
                        to="/teams/$id"
                        params={{ id: t.teamId }}
                        className="text-neutral-200 hover:text-orange-400"
                      >
                        {t.teamName}
                      </Link>
                      <span className="ml-1 text-xs text-neutral-500">/{t.teamSlug}</span>
                    </td>
                    <td className="py-1 text-neutral-400">{t.planCode}</td>
                    <td className="py-1 text-right text-neutral-200">{formatBytes(t.storageUsedBytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-neutral-500">Loading…</p>
          )}
        </div>
      </section>
    </AdminLayout>
  );
}

function Tile(props: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
      <div className="text-xs uppercase tracking-wider text-neutral-500">{props.label}</div>
      <div className="mt-1 text-2xl font-semibold text-neutral-100">{props.value}</div>
      {props.hint ? <div className="mt-1 text-[10px] text-neutral-600">{props.hint}</div> : null}
    </div>
  );
}

function TimeseriesChart({
  data,
}: {
  data: Array<{ date: string; signups: number; newSubscriptions: number; cancellations: number }>;
}) {
  if (data.length === 0) return null;
  const w = 800;
  const h = 120;
  const pad = 20;
  const max = Math.max(
    1,
    ...data.flatMap((d) => [d.signups, d.newSubscriptions, d.cancellations]),
  );
  const step = (w - pad * 2) / Math.max(1, data.length - 1);

  function buildPath(key: 'signups' | 'newSubscriptions' | 'cancellations'): string {
    return data
      .map((d, i) => {
        const x = pad + i * step;
        const y = h - pad - ((h - pad * 2) * d[key]) / max;
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-32 w-full">
        <path d={buildPath('signups')} stroke="#3b82f6" strokeWidth="2" fill="none" />
        <path d={buildPath('newSubscriptions')} stroke="#10b981" strokeWidth="2" fill="none" />
        <path d={buildPath('cancellations')} stroke="#ef4444" strokeWidth="2" fill="none" />
      </svg>
      <div className="mt-2 flex items-center gap-4 text-xs text-neutral-400">
        <Legend color="#3b82f6" label={`Signups: ${data.reduce((a, b) => a + b.signups, 0)}`} />
        <Legend color="#10b981" label={`New subs: ${data.reduce((a, b) => a + b.newSubscriptions, 0)}`} />
        <Legend color="#ef4444" label={`Cancellations: ${data.reduce((a, b) => a + b.cancellations, 0)}`} />
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-block size-2 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(0)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

// ── Subscription health ──────────────────────────────────────────────────

/**
 * Subscription-focused widget cluster for the metrics dashboard.  Three blocks:
 *
 *   1. Status breakdown — count per status, sized by row so a glance reveals
 *      "12 trials, 87 active, 3 past_due".  Past_due is amber so the eye
 *      lands on the row that needs attention.
 *
 *   2. MRR per plan — table of plan → monthly recurring revenue (cents → $).
 *      Phase 1 MRR is "potential" (manual grants land at unitAmountCents=0
 *      unless the operator filled it in).  When Stripe wires up the real
 *      paid prices flow through here automatically.
 *
 *   3. Needs attention — chip-row of trial-expiring + past_due counters
 *      with a drill-down list.  Each entry links to the subscription detail
 *      page so the operator can extend / expire / chase the customer in
 *      one click.
 */
function AiUsageSection({ usage }: { usage: AiUsageSummary | undefined }) {
  return (
    <section className="mb-6 rounded-lg border border-neutral-800 bg-neutral-900 p-5">
      <h2 className="mb-3 text-sm font-medium text-neutral-300">AI usage — this month</h2>
      {usage === undefined ? (
        <p className="text-sm text-neutral-500">Loading…</p>
      ) : (
        <>
          <div className="mb-4 grid gap-3 md:grid-cols-2">
            <Tile
              label="AI cost (this month)"
              value={`$${usage.monthCostUsd.toFixed(2)}`}
              hint="User-facing AI + agent runs, teams only (Personal AI is never shown)"
            />
            <Tile
              label="AI actions (this month)"
              value={usage.monthActions.toLocaleString()}
              hint="AI calls + agent runs"
            />
          </div>
          {usage.topTeams.length === 0 ? (
            <p className="text-sm text-neutral-500">No AI usage yet this month.</p>
          ) : (
            <div>
              <div className="mb-1.5 text-xs uppercase tracking-wider text-neutral-500">
                Top teams by AI cost
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500">
                    <th className="py-1 font-normal">Team</th>
                    <th className="py-1 text-right font-normal">Actions</th>
                    <th className="py-1 text-right font-normal">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.topTeams.map((t) => (
                    <tr key={t.teamId} className="border-t border-neutral-800">
                      <td className="py-1.5 text-neutral-200">
                        {t.teamName ?? `${t.teamId.slice(0, 8)}…`}
                      </td>
                      <td className="py-1.5 text-right text-neutral-400">
                        {t.actions.toLocaleString()}
                      </td>
                      <td className="py-1.5 text-right text-neutral-200">${t.costUsd.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function SubscriptionHealthSection({
  breakdown,
  needsAttention,
}: {
  breakdown: SubscriptionBreakdown | undefined;
  needsAttention: SubscriptionNeedingAttention[] | undefined;
}) {
  return (
    <section className="mb-6 grid gap-4 lg:grid-cols-3">
      {/* Status breakdown */}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
        <h2 className="mb-3 text-sm font-medium text-neutral-300">Subscription status</h2>
        {breakdown ? (
          <ul className="space-y-1.5">
            {(
              ['active', 'trial', 'lifetime', 'past_due', 'cancelled', 'free'] as SubscriptionStatus[]
            ).map((s) => (
              <li key={s} className="flex items-center justify-between text-sm">
                <span
                  className={
                    s === 'past_due'
                      ? 'text-amber-400'
                      : s === 'cancelled'
                        ? 'text-neutral-500'
                        : 'text-neutral-300'
                  }
                >
                  {STATUS_LABELS[s]}
                </span>
                <code
                  className={
                    s === 'past_due'
                      ? 'rounded bg-amber-500/10 px-1.5 py-0.5 text-xs text-amber-300'
                      : 'rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-neutral-200'
                  }
                >
                  {breakdown.byStatus[s]}
                </code>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-neutral-500">Loading…</p>
        )}
      </div>

      {/* MRR by plan */}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
        <h2 className="mb-3 text-sm font-medium text-neutral-300">MRR by plan (monthly)</h2>
        {breakdown ? (
          breakdown.mrrByPlan.length === 0 ? (
            <p className="text-sm text-neutral-500">
              No paid monthly subscriptions yet.  When Stripe wires up, this populates from
              real charged amounts.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="py-1 font-medium">Plan</th>
                  <th className="py-1 text-right font-medium">Subs</th>
                  <th className="py-1 text-right font-medium">MRR</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.mrrByPlan.map((m) => (
                  <tr key={m.planCode}>
                    <td className="py-1">
                      <code className="text-orange-400">{m.planCode}</code>
                    </td>
                    <td className="py-1 text-right text-neutral-400">{m.subscriptions}</td>
                    <td className="py-1 text-right text-neutral-200">
                      ${(m.cents / 100).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          <p className="text-sm text-neutral-500">Loading…</p>
        )}
      </div>

      {/* Actionable counters */}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
        <h2 className="mb-3 text-sm font-medium text-neutral-300">Needs attention</h2>
        {breakdown ? (
          <div className="space-y-3">
            <AttentionChip
              icon={Clock}
              tone="amber"
              count={breakdown.trialsExpiringSoon}
              label="Trials expiring this week"
            />
            <AttentionChip
              icon={AlertTriangle}
              tone="red"
              count={breakdown.pastDueCount}
              label="Past due (action required)"
            />
            <AttentionChip
              icon={DollarSign}
              tone="neutral"
              count={breakdown.byStatus.active + breakdown.byStatus.lifetime}
              label="Total paid / lifetime"
            />
          </div>
        ) : (
          <p className="text-sm text-neutral-500">Loading…</p>
        )}

        {needsAttention && needsAttention.length > 0 ? (
          <div className="mt-4 border-t border-neutral-800 pt-3">
            <div className="mb-2 text-[10px] uppercase tracking-wider text-neutral-500">
              Drill-down ({needsAttention.length})
            </div>
            <ul className="max-h-48 space-y-1 overflow-y-auto pr-1">
              {needsAttention.slice(0, 12).map((row) => (
                <li key={row.id}>
                  <Link
                    to="/subscriptions/$id"
                    params={{ id: row.id }}
                    className="block rounded-md bg-neutral-950/40 px-2 py-1.5 text-xs hover:bg-neutral-800/60"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={
                          row.reason === 'past_due'
                            ? 'rounded bg-red-500/15 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-red-300'
                            : 'rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-amber-300'
                        }
                      >
                        {row.reason === 'past_due' ? 'past due' : 'trial ends'}
                      </span>
                      <span className="truncate text-neutral-300">{row.customerLabel}</span>
                      <code className="ml-auto shrink-0 text-[10px] text-neutral-500">
                        {row.planCode}
                      </code>
                    </div>
                    <div className="mt-0.5 text-[10px] text-neutral-500">
                      {row.reason === 'past_due' ? 'since ' : 'at '}
                      {new Date(row.attentionAt).toLocaleDateString()}
                    </div>
                  </Link>
                </li>
              ))}
              {needsAttention.length > 12 ? (
                <li className="px-2 py-1 text-[10px] italic text-neutral-600">
                  …and {needsAttention.length - 12} more — see{' '}
                  <Link to="/subscriptions" className="text-orange-400 hover:underline">
                    Subscriptions
                  </Link>
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
}

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  free: 'Free',
  trial: 'Trial',
  active: 'Active',
  past_due: 'Past due',
  cancelled: 'Cancelled',
  lifetime: 'Lifetime',
};

function AttentionChip({
  icon: Icon,
  tone,
  count,
  label,
}: {
  icon: LucideIcon;
  tone: 'amber' | 'red' | 'neutral';
  count: number;
  label: string;
}) {
  const toneClasses = {
    amber:
      count > 0
        ? 'bg-amber-500/10 text-amber-300 ring-amber-500/30'
        : 'bg-neutral-800 text-neutral-500 ring-neutral-700',
    red:
      count > 0
        ? 'bg-red-500/10 text-red-300 ring-red-500/30'
        : 'bg-neutral-800 text-neutral-500 ring-neutral-700',
    neutral: 'bg-neutral-800 text-neutral-300 ring-neutral-700',
  }[tone];
  return (
    <div className={`flex items-center justify-between rounded-md px-3 py-2 ring-1 ${toneClasses}`}>
      <div className="flex items-center gap-2 text-xs">
        <Icon size={12} />
        {label}
      </div>
      <code className="text-sm font-semibold">{count}</code>
    </div>
  );
}
